import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import PrimaryTextField from "../Inputs/PrimaryTextField/PrimaryTextField";
import DynamicTabPanel from "../TabPanels/DynamicTabPanel/DynamicTabPanel";
import LoadBoard from "./LoadBoard/LoadBoard";
import LoadBoardFilters from "./LoadBoard/LoadBoardFilters/LoadBoardFilters";
import LoadBoardColumns from "./LoadBoard/LoadBoardColumns/LoadBoardColumns";
import { CustomLoadBoardService } from "../../services/CustomLoadBoardService";
import { ActiveUserContext, AlertContext } from "../Dashboard/Dashboard";
import { sortArrayOfNumbersAsc } from "../../utils/sortHelpers";
import { formatTitleCase } from "../../utils/formatHelpers";

export default function LoadBoards({
  isAdminUser,
  isLoadBoardLoading,
  allLoads,
  deleteNewLoads,
  onLoadFieldUpdate,
  handleTableAction,
  useLoadBoardsV2,
  setUseLoadBoardsV2
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [tabLabels, setTabLabels] = useState([
    { customLoadBoardByUserID: "All", customLoadBoardName: "All", disableDelete: true },
    { customLoadBoardByUserID: "Tracking", customLoadBoardName: "Tracking", disableDelete: true },
  ]);
  const [newTabAdded, setNewTabAdded] = useState(false);
  const [tabDeleted, setTabDeleted] = useState(null);
  const [filters, setFilters] = useState([]);
  const [columns, setColumns] = useState([]);
  const [search, setSearch] = useState("");
  const [totalResults, setTotalResults] = useState(0);

  const defaultColumns = useMemo(() => [
    {
      title: "Status",
      columnName: "brokerageStatus",
      isChecked: true,
      displayOrder: 1,
      trackingOrder: 6
    },
    {
      title: "Load",
      columnName: "orderId",
      isChecked: true,
      displayOrder: 2,
      trackingOrder: 4
    },
    {
      title: "Carrier",
      columnName: "carrierName",
      isChecked: true,
      displayOrder: 3,
      trackingOrder: 3
    },
    {
      title: "Order",
      columnName: "orderTypeId",
      isChecked: true,
      displayOrder: 4,
      trackingOrder: 7
    },
    {
      title: "BOL #",
      columnName: "blnum",
      isChecked: true,
      displayOrder: 5
    },
    {
      title: "Tender",
      columnName: "tenderType",
      isChecked: true,
      displayOrder: 6
    },
    {
      title: "Customer",
      columnName: "customerName",
      isChecked: true,
      displayOrder: 7,
      trackingOrder: 19
    },
    {
      title: "Mode",
      columnName: "mode",
      isChecked: true,
      displayOrder: 8,
      trackingOrder: 21
    },
    {
      title: "Origin City",
      columnName: "originCity",
      isChecked: true,
      displayOrder: 9,
      trackingOrder: 14
    },
    {
      title: "OS",
      columnName: "originState",
      isChecked: true,
      displayOrder: 10,
      trackingOrder: 15
    },
    {
      title: "Pick Up",
      columnName: "originSchedArriveEarly",
      isChecked: true,
      displayOrder: 11,
      trackingOrder: 13
    },
    {
      title: "Dest City",
      columnName: "destinationCity",
      isChecked: true,
      displayOrder: 12,
      trackingOrder: 17
    },
    {
      title: "DS",
      columnName: "destinationState",
      isChecked: true,
      displayOrder: 13,
      trackingOrder: 18
    },
    {
      title: "Delivery",
      columnName: "destinationSchedArriveEarly",
      isChecked: true,
      displayOrder: 14,
      trackingOrder: 16
    },
    {
      title: "Miles",
      columnName: "miles",
      isChecked: true,
      displayOrder: 15,
      trackingOrder: 22
    },
    {
      title: "Weight",
      columnName: "weight",
      isChecked: true,
      displayOrder: 16
    },
    {
      title: "Commodity",
      columnName: "commodity",
      isChecked: true,
      displayOrder: 17
    },
    {
      title: "Customer Rate",
      columnName: "customerTotalCharges",
      isChecked: true,
      displayOrder: 18
    },
    {
      title: "Carrier Pay",
      columnName: "carrierPay",
      isChecked: true,
      displayOrder: 19
    },
    {
      title: "Gross Profit",
      columnName: "grossProfit",
      isChecked: true,
      displayOrder: 20
    },
    {
      title: "Max Pay",
      columnName: "maxPay",
      isChecked: true,
      displayOrder: 21
    },
    {
      title: "Offers",
      columnName: "offerCount",
      isChecked: true,
      displayOrder: 22
    },
    {
      title: "Carrier Rep",
      columnName: "distpatcherUserId",
      isChecked: true,
      displayOrder: 23,
      trackingOrder: 2
    },
    {
      title: "Acct Mgr",
      columnName: "accountMgr",
      isChecked: true,
      displayOrder: 24,
      trackingOrder: 20
    },
    {
      title: "Rate Con",
      columnName: "rateConfirmationStatus",
      isChecked: true,
      displayOrder: 25
    },
    {
      title: "Rate Con Sent",
      columnName: "rateConfirmationSentDate",
      isChecked: true,
      displayOrder: 26,
      trackingOrder: 23
    },
    {
      title: "ETA to Next Stop",
      columnName: "newEta",
      isChecked: true,
      displayOrder: 27,
      trackingOrder: 11
    },
    {
      title: "ETA Status",
      columnName: "etaStatus",
      isChecked: true,
      displayOrder: 28,
      trackingOrder: 10
    },
    {
      title: "Next Stop",
      columnName: "nextStop",
      isChecked: true,
      displayOrder: 29
    },
    {
      title: "Last callin Date",
      columnName: "lastCallDateTime",
      isChecked: true,
      displayOrder: 30,
      trackingOrder: 1
    },
    {
      title: "Current Location",
      columnName: "currentLocation",
      isChecked: true,
      displayOrder: 31,
      trackingOrder: 8
    },
    {
      title: "Tracking Status",
      columnName: "trackStatus",
      isChecked: true,
      displayOrder: 32,
      trackingOrder: 5
    },
    {
      title: "Next Scheduled Check Call",
      columnName: "nextSchedCall",
      isChecked: true,
      displayOrder: 33,
      trackingOrder: 12
    },
    {
      title: "Distance To Next Stop",
      columnName: "distanceNextStop",
      isChecked: true,
      displayOrder: 34,
      trackingOrder: 9
    },
  ], []);

  const handleTabChange = useCallback((newValue) => {
    setIsLoading(true);
    setTabValue(newValue);
    setSearch("");
    if (newValue === 0) {
      setFilters([]);
      setColumns(defaultColumns);
    } else if (newValue === 1) {
      let regionName = activeUser.region?.regionName;
      regionName = regionName === "Great Plains" ? "Plains States" : formatTitleCase(regionName?.replace(/\s+/g, ""));
      setFilters([
        {
          fieldName: "daysBack",
          params: "60"
        },
        {
          fieldName: "daysOut",
          params: "30"
        },
        {
          fieldName: "brokerageStatus",
          params: "COV||COVCOM||DEL||DIS||LOA||LOAD"
        },
        {
          fieldName: "etaStatus",
          params: "-1||1||2||3||4"
        },
        {
          fieldName: isAdminUser ? "destinationZoneName" : "distpatcherUserId",
          params: isAdminUser ? regionName : activeUser.userIdMcld
        }
      ]);

      const defCols = defaultColumns
        .filter(({ trackingOrder }) => !trackingOrder)
        .map(defCol => { return { ...defCol, isChecked: false }; });

      let columns = defaultColumns.filter(({ trackingOrder }) => trackingOrder);
      columns = sortArrayOfNumbersAsc(columns, "trackingOrder");
      columns = columns.concat(defCols);
      setColumns(columns);
    } else {
      const filters = tabLabels[newValue].customLoadBoardByUserFilters;
      setFilters(filters);
      let columns = tabLabels[newValue].customLoadBoardByUserColumns;
      if (columns.length > 0) {
        const defCols = defaultColumns
          .filter(({ columnName }) => !columns.some(col => col.columnName === columnName))
          .map(defCol => { return { ...defCol, isChecked: false }; });

        columns = sortArrayOfNumbersAsc(columns, "displayOrder");
        columns = columns.concat(defCols);
        columns.forEach((col, i) => {
          col.displayOrder = i + 1;

          const defCol = defaultColumns.find(({ columnName }) => columnName === col.columnName);
          if (defCol) {
            col.title = defCol.title;
          }
        });
      }
      setColumns(columns);
    }
    setIsLoading(false);
  }, [defaultColumns, tabLabels]);

  const handleTabCreate = useCallback((name) => {
    let payload = {
      userObjectId: activeUser.userId,
      customLoadBoardName: name,
      isActive: true,
      customLoadBoardByUserFilters: filters,
      customLoadBoardByUserColumns: columns
    };

    setIsLoading(true);
    CustomLoadBoardService.CreateCustomLoadBoard(instance, payload).then(res => {
      const tabs = [...tabLabels];
      localStorage.setItem("newTabCreated", true);
      localStorage.setItem("newTabCreated2", true);
      tabs.push(res);
      setTabLabels(tabs);
      setNewTabAdded(true);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  }, [
    activeUser,
    columns,
    filters,
    tabLabels
  ]);

  const handleTabDelete = useCallback((index) => {
    const payload = tabLabels[index];
    payload.isActive = false;

    setIsLoading(true);
    CustomLoadBoardService.UpdateCustomLoadBoard(instance, payload).then(() => {
      localStorage.setItem("deletedTab", true);
      if (localStorage.getItem(`loadboard-tab-${payload.customLoadBoardByUserID}`)) {
        localStorage.removeItem(`loadboard-tab-${payload.customLoadBoardByUserID}`);
      }
      if (localStorage.getItem(`sorting-tab-${payload.customLoadBoardByUserID}`)) {
        localStorage.removeItem(`sorting-tab-${payload.customLoadBoardByUserID}`);
      }

      const tabs = [...tabLabels];
      tabs.splice(index, 1);
      setTabLabels(tabs);
      setTabDeleted(index);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  }, [tabLabels]);

  useEffect(() => {
    if (newTabAdded) {
      handleTabChange(tabLabels.length - 1);
      setNewTabAdded(false);
    }
  }, [newTabAdded]);

  useEffect(() => {
    if (tabDeleted) {
      setTabDeleted(null);
      if (tabDeleted === tabValue) handleTabChange(0);
      else if (tabDeleted < tabValue) handleTabChange(tabValue - 1);
    }
  }, [tabDeleted]);

  useEffect(() => {
    if (tabValue != 0 && tabValue != 1) {
      const payload = tabLabels[tabValue];
      payload.customLoadBoardByUserFilters = filters;
      payload.customLoadBoardByUserColumns = columns;

      CustomLoadBoardService.UpdateCustomLoadBoard(instance, payload).then(res => {
        const tabs = [...tabLabels];
        tabs[tabValue] = res;
        setTabLabels(tabs);
      }).catch(error => {
        console.error(error);
        warning(error);
      });
    }
  }, [filters, columns]);

  useEffect(() => {
    if (activeUser.userId) {
      setIsLoading(true);
      CustomLoadBoardService.GetCustomLoadBoardsByUser(instance, activeUser.userId).then(res => {
        if (res.length > 0) {
          const tabs = [...tabLabels];
          res.filter(({ isActive }) => isActive)
            .forEach(board => {
              tabs.push(board);
            });
          setTabLabels(tabs);
        }
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, [activeUser]);

  return (
    <>
      <Box sx={{ display: "flex", borderBottom: 1, borderColor: "divider", margin: "10px 30px 10px 30px" }}>
        <Box sx={{ display: "flex", flexGrow: 1 }}>
          <DynamicTabPanel
            isLoading={isLoading || isLoadBoardLoading}
            tabLabels={tabLabels}
            value={tabValue}
            nameKey="customLoadBoardName"
            handleTabChange={handleTabChange}
            handleTabCreate={handleTabCreate}
            handleTabDelete={handleTabDelete}
            maxTabs={7}
          />
        </Box>
        <Box sx={{ display: "flex", margin: "5px 0 0 auto" }}>
          {tabValue != 1 &&
            <Box marginRight="10px">
              <LoadBoardColumns
                tabValue={tabValue}
                columns={columns}
                setColumns={setColumns}
                defaultColumns={defaultColumns}
              />
            </Box>
          }
          <Box marginRight="10px">
            <LoadBoardFilters
              activeUser={activeUser}
              isAdminUser={isAdminUser}
              tabValue={tabValue}
              allLoads={allLoads}
              filters={filters}
              setFilters={setFilters}
            />
          </Box>
          <Box>
            <PrimaryTextField
              placeholder={`Search ${totalResults} results`}
              onChange={setSearch}
              value={search}
              isSearch
            />
          </Box>
          <Box sx={{ ml: 1 }}>
            <Tooltip title="Switch between LoadBoards V1 and V2">
              <FormControlLabel
                control={
                  <Switch
                    checked={useLoadBoardsV2}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setUseLoadBoardsV2(newValue);
                      localStorage.setItem("useLoadBoardsV2", JSON.stringify(newValue));
                    }}
                    size="small"
                  />
                }
                label="V2"
              />
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <Box>
        <LoadBoard
          isAdminUser={isAdminUser}
          tabLabels={tabLabels}
          tabValue={tabValue}
          isLoading={isLoading || isLoadBoardLoading}
          allLoads={allLoads}
          deleteNewLoads={deleteNewLoads}
          onLoadFieldUpdate={onLoadFieldUpdate}
          customFilters={filters}
          customColumns={columns}
          search={search}
          setTotalResults={setTotalResults}
          handleTableAction={handleTableAction}
        />
      </Box>
    </>
  );
}

LoadBoards.propTypes = {
  isAdminUser: PropTypes.bool,
  isLoadBoardLoading: PropTypes.bool.isRequired,
  allLoads: PropTypes.array.isRequired,
  deleteNewLoads: PropTypes.func.isRequired,
  onLoadFieldUpdate: PropTypes.func,
  handleTableAction: PropTypes.func,
  useLoadBoardsV2: PropTypes.bool,
  setUseLoadBoardsV2: PropTypes.func,
};